import { useQuery } from "@tanstack/react-query";
import type { BaselineDeliverable } from "@/hooks/use-baseline";
import type { Solution } from "@/hooks/use-solutions";

export interface ReportEvidence {
  id: string;
  externalKey: string | null;
  extracted: string;
  quote: string;
  sourceId: string;
}

export interface OutOfScopeGroup {
  reason: string;
  items: { id: string; externalKey: string | null; extracted: string; quote: string }[];
}

export interface ReportPayload {
  solution: Solution;
  counts: {
    total: number;
    decided: number;
    inScope: number;
    outOfScope: number;
    inReview: number;
  };
  baseline: BaselineDeliverable[];
  outOfScopeByReason: OutOfScopeGroup[];
  questions: Array<{
    id: string;
    text: string;
    answer: string | null;
    priority: string;
    status: string;
    raisedAt: string;
  }>;
  conflicts: Array<{
    id: string;
    description: string;
    status: string;
    conflictDate: string;
  }>;
  stakeholders: Array<{
    id: string;
    name: string;
    role: string;
    org: "client" | "nymbl";
    initials: string;
  }>;
  evidence: ReportEvidence[];
}

const reportKeys = {
  one: (solutionId: string) => ["report", solutionId] as const,
} as const;

export function useReport(solutionId: string) {
  return useQuery({
    queryKey: reportKeys.one(solutionId),
    queryFn: async (): Promise<ReportPayload> => {
      const res = await fetch(`/api/solutions/${solutionId}/report`);
      if (!res.ok) throw new Error("Failed to load report");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(solutionId),
  });
}
